/**
 * AgentInputArea Component
 * Input section with textarea and send button
 */

import React, { useState, useCallback, useRef, useEffect, KeyboardEvent } from 'react'
import { Send, Loader2 } from 'lucide-react'
import { AgentQuickActionsBar } from './AgentQuickActionsBar'
import type { AgentQuickAction } from '../../types'

interface AgentInputAreaProps {
  /**
   * Quick actions available for this document type
   */
  quickActions: AgentQuickAction[]
  /**
   * Callback when a message is sent
   */
  onSendMessage: (message: string) => void
  /**
   * Callback when a quick action is selected
   */
  onQuickAction?: (action: AgentQuickAction) => void
  /**
   * Whether the input is disabled
   */
  disabled?: boolean
  /**
   * Whether the agent is currently processing
   */
  isLoading?: boolean
  /**
   * Placeholder text for the input
   */
  placeholder?: string
}

/**
 * Input area with auto-expanding textarea and quick actions
 */
export const AgentInputArea: React.FC<AgentInputAreaProps> = ({
  quickActions,
  onSendMessage,
  onQuickAction,
  disabled = false,
  isLoading = false,
  placeholder = 'Type your message...',
}) => {
  const [message, setMessage] = useState('')
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  // Auto-resize textarea based on content
  useEffect(() => {
    const textarea = textareaRef.current
    if (textarea) {
      textarea.style.height = 'auto'
      textarea.style.height = `${Math.min(textarea.scrollHeight, 120)}px`
    }
  }, [message])

  const handleSubmit = useCallback(() => {
    const trimmedMessage = message.trim()
    if (trimmedMessage && !disabled && !isLoading) {
      onSendMessage(trimmedMessage)
      setMessage('')
      // Reset textarea height
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto'
      }
    }
  }, [message, disabled, isLoading, onSendMessage])

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>) => {
      // Send on Enter (without Shift)
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault()
        handleSubmit()
      }
    },
    [handleSubmit]
  )

  const handleQuickAction = useCallback(
    (action: AgentQuickAction) => {
      if (onQuickAction) {
        onQuickAction(action)
      } else {
        // Default: send the action label as a message
        onSendMessage(action.label)
      }
    },
    [onQuickAction, onSendMessage]
  )

  return (
    <div className="border-t border-gray-200 bg-white">
      {/* Quick Actions */}
      <AgentQuickActionsBar
        actions={quickActions}
        onActionClick={handleQuickAction}
        disabled={disabled || isLoading}
      />

      {/* Input Area */}
      <div className="flex items-end gap-2 p-4">
        <div className="relative flex-1">
          <textarea
            ref={textareaRef}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={disabled ? 'Select an agent to start' : placeholder}
            disabled={disabled || isLoading}
            rows={1}
            className="
              w-full resize-none rounded-xl border border-gray-300 bg-gray-50
              px-4 py-3 pr-12 text-sm text-gray-800 placeholder-gray-400
              transition-colors
              focus:border-blue-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20
              disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-400
            "
            aria-label="Message input"
          />
        </div>

        <button
          type="button"
          onClick={handleSubmit}
          disabled={!message.trim() || disabled || isLoading}
          className="
            flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl
            bg-blue-500 text-white transition-all
            hover:bg-blue-600
            disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-400
          "
          title="Send message (Enter)"
          aria-label="Send message"
        >
          {isLoading ? (
            <Loader2 className="h-5 w-5 animate-spin" />
          ) : (
            <Send className="h-5 w-5" />
          )}
        </button>
      </div>

      {/* Keyboard hint */}
      <div className="px-4 pb-3">
        <p className="text-center text-xs text-gray-400">
          Press <kbd className="rounded bg-gray-100 px-1 font-mono">Enter</kbd> to send,{' '}
          <kbd className="rounded bg-gray-100 px-1 font-mono">Shift+Enter</kbd> for new line
        </p>
      </div>
    </div>
  )
}

export default AgentInputArea
